import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  IntegrationsJoinCreateOrderDocument,
  JoinPremiumProductFragment,
  JoinPremiumSubProductFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CheckoutModal } from 'modules/ats/pages/JobPostingMarketplace/CheckoutModal'
import { CheckoutProductDetail } from 'modules/ats/pages/JobPostingMarketplace/CheckoutModal/CheckoutProductDetail'

interface Props {
  product: JoinPremiumProductFragment
  subProduct: JoinPremiumSubProductFragment
  onClose: () => void
  isOpen: boolean
}

export const ProductCheckoutModal = ({ product, subProduct, onClose, isOpen }: Props) => {
  const { jobId } = useParams()
  const { company, access } = useCurrentSession()
  const [createOrder, { loading: creatingOrder }] = useMutation(
    IntegrationsJoinCreateOrderDocument,
    {
      extractorFn: (response) => response.integrationsJoinMutations.createIntegrationsjoinOrder,
    }
  )
  const hasDiscount = subProduct.pricing.reducedPercentage > 0
  const productPrice = hasDiscount
    ? subProduct.pricing.reducedAmount
    : subProduct.pricing.originalAmount

  const buildProductSnapshotProducts = () => {
    return [
      {
        description: product.description,
        durationDays: subProduct.durationDays,
        jobBoard: {
          slug: product.slug,
          name: product.name,
          logoUrl: product.logoUrl,
        },
      },
    ]
  }

  const handleCreateOrder = async (requesterNote: string | undefined) => {
    const response = await createOrder({
      variables: {
        accessId: access.id,
        atsJobPostingId: Number(jobId),
        premiumOfferId: subProduct.id,
        premiumOfferName: product.name,
        premiumOfferPrice: productPrice,
        premiumOfferType: 'product',
        note: requesterNote,
        snapshot: {
          price: productPrice,
          currency: subProduct.pricing.currencyCode,
          products: buildProductSnapshotProducts(),
        },
      },
    })

    const handled = await handleResult(response, (error) => {
      app.setFlash({
        type: 'wrong',
        message: error.messages || i18n.t('common.save_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      track(trackingEvents.ats.premiumOfferPurchased, {
        company_id: company.id,
        offer_id: subProduct.id,
        offer_name: product.name,
        offer_type: 'product',
        offer_product_name: subProduct.name,
        price: productPrice,
      })
      onClose()
    }

    return handled
  }

  return (
    <CheckoutModal
      pricing={subProduct.pricing}
      isOpen={isOpen}
      onClose={onClose}
      isSubmitting={creatingOrder}
      onSubmit={(requesterNote) => handleCreateOrder(requesterNote)}
    >
      <Box flexDirection="column" gap="s12">
        <Text.Normal size="400" weight="semibold" color="grey1000">
          {product.name}
        </Text.Normal>

        <CheckoutProductDetail
          key={`modal-product-${subProduct.id}`}
          offerName={subProduct.name}
          durationDays={subProduct.durationDays}
          jobBoard={{
            slug: product.slug,
            name: product.name,
            description: product.description,
            logoUrl: product.logoUrl,
            hoursToProcess: product.hoursToProcess,
          }}
        />
      </Box>
    </CheckoutModal>
  )
}
